/**
 * Adding a widget, changing one, and taking one away.
 *
 * **Every one of these has to write the cache itself.** All four dashboard
 * events are broadcast to the dashboard's group with the acting tab's
 * `web_socket_id` as the ignore id, so the tab that made the change hears
 * nothing back about it -- the same arrangement the shell's workspace menus are
 * under, and the reason none of them wait for the socket either.
 *
 * Nothing here is optimistic. A widget's id is the server's -- the data source
 * it reads is made at the same moment and named by that id -- so a placeholder
 * card would be a card whose number cannot be asked for until the answer
 * arrives. The wait is one round trip and the alternative is an empty card that
 * cannot be configured for the length of it.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { getApi, unwrap } from '@/api/client'
import { qk } from '@/api/query-keys'
import type { ChartType, Widget, WidgetTypeName } from '@/api/types'
import { inOrder, withWidget, withoutWidget } from './cache'

/** What a widget's settings form can change. */
export interface WidgetChanges {
  title?: string
  description?: string
  chart_type?: ChartType
}

export interface WidgetMutations {
  create: (
    widget: { title: string; type: WidgetTypeName; chart_type?: ChartType },
    onCreated: (widget: Widget) => void,
  ) => void
  update: (widgetId: number, changes: WidgetChanges) => void
  remove: (widgetId: number) => void
  isPending: boolean
}

export function useWidgetMutations(dashboardId: number): WidgetMutations {
  const queryClient = useQueryClient()
  const key = qk.widgets(dashboardId)

  const store = (widget: Widget): void => {
    queryClient.setQueryData<Widget[]>(key, (held) =>
      held === undefined ? held : withWidget(held, widget),
    )
  }

  const create = useMutation({
    mutationFn: (body: {
      title: string
      type: WidgetTypeName
      chart_type?: ChartType
    }): Promise<Widget> =>
      unwrap(
        getApi().POST('/api/dashboard/{dashboard_id}/widgets/', {
          params: { path: { dashboard_id: dashboardId } },
          // Cast at the boundary, like every polymorphic payload: the body is a
          // union over the widget types and `chart_type` belongs to one of them.
          body: body as never,
        }),
      ),
    onSuccess: (widget) => {
      store(widget)
      // Creating a widget creates the data source it reads, server-side, and
      // the answer carries only the widget. Without this the new card arrives
      // saying its data source is gone, when it has simply never been fetched.
      void queryClient.invalidateQueries({ queryKey: qk.dataSources(dashboardId) })
    },
  })

  const update = useMutation({
    mutationFn: ({
      widgetId,
      changes,
    }: {
      widgetId: number
      changes: WidgetChanges
    }): Promise<Widget> =>
      unwrap(
        getApi().PATCH('/api/dashboard/widgets/{widget_id}/', {
          params: { path: { widget_id: widgetId } },
          body: changes as never,
        }),
      ),
    onSuccess: store,
  })

  const remove = useMutation({
    mutationFn: (widgetId: number): Promise<unknown> =>
      unwrap(
        getApi().DELETE('/api/dashboard/widgets/{widget_id}/', {
          params: { path: { widget_id: widgetId } },
        }),
      ),
    // A 204 that says nothing, so what went is read from what was asked.
    onSuccess: (_answer, widgetId) => {
      const held = queryClient
        .getQueryData<Widget[]>(key)
        ?.find((one) => one.id === widgetId)

      queryClient.setQueryData<Widget[]>(key, (widgets) =>
        widgets === undefined ? widgets : withoutWidget(widgets, widgetId),
      )
      void queryClient.invalidateQueries({ queryKey: qk.dataSources(dashboardId) })

      // The answer the deleted data source gave is dropped rather than marked
      // stale, for the reason the realtime handler drops it: nothing is
      // subscribed to it any more, and an invalidation would leave the number
      // cached under an id the server is free to hand to the next data source.
      const sourceId = held?.data_source_id
      if (typeof sourceId === 'number') {
        queryClient.removeQueries({
          queryKey: qk.dataSourceResult(dashboardId, sourceId),
        })
      }
    },
  })

  return {
    create: (widget, onCreated) => {
      create.mutate(widget, { onSuccess: onCreated })
    },
    update: (widgetId, changes) => {
      update.mutate({ widgetId, changes })
    },
    remove: (widgetId) => {
      remove.mutate(widgetId)
    },
    isPending: create.isPending || update.isPending || remove.isPending,
  }
}

/**
 * Rearranging a board. Optimistic, unlike the rest: the order is the client's to
 * decide, and a card that sprang back for the length of a round trip would read
 * as a drop that failed.
 */
export function useOrderWidgets(dashboardId: number) {
  const queryClient = useQueryClient()
  const key = qk.widgets(dashboardId)

  return useMutation({
    mutationFn: (widgetIds: number[]): Promise<unknown> =>
      unwrap(
        getApi().POST('/api/dashboard/{dashboard_id}/widgets/order/', {
          params: { path: { dashboard_id: dashboardId } },
          body: { widget_ids: widgetIds },
        }),
      ),
    onMutate: (widgetIds) => {
      const previous = queryClient.getQueryData<Widget[]>(key)
      queryClient.setQueryData<Widget[]>(key, (held) =>
        held === undefined ? held : inOrder(held, widgetIds),
      )
      return { previous }
    },
    onError: (_error, _widgetIds, context) => {
      queryClient.setQueryData(key, context?.previous)
    },
  })
}
